import { objectSpread2 as _objectSpread2, asyncToGenerator as _asyncToGenerator, regeneratorRuntime as _regeneratorRuntime } from './_virtual/_rollupPluginBabelHelpers.mjs';
import { delay } from '@toss/utils';
import { SPEED_DEFAULT } from './AnimationData.mjs';

function AnimationPlayer(json, options) {
  var lottieObject;
  var _speed = SPEED_DEFAULT;
  var shouldLoop = Boolean(options.loop);
  var shouldLoopWithInterval = shouldLoop && Boolean(options.interval);
  var shouldLoopWithReverse = shouldLoop && options.loop === 'reversed';
  return {
    load: function load() {
      if (lottieObject !== undefined) {
        return;
      }

      lottieObject = options.lottie.loadAnimation({
        container: options.container,
        animationData: replaceAssets(json, options.assets, {
          lottieUrl: options.lottieUrl
        }),
        loop: shouldLoop && !shouldLoopWithInterval && !shouldLoopWithReverse,
        renderer: 'svg',
        autoplay: true
      });
      lottieObject.setSpeed(_speed);
    },
    showFirstFrame: function showFirstFrame() {
      if (lottieObject === undefined) {
        return;
      }

      lottieObject.goToAndStop(0);
    },
    play: function play() {
      return new Promise(function (resolve) {
        if (lottieObject === undefined) {
          resolve();
          return;
        }

        var handleComplete = /*#__PURE__*/function () {
          var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
            var _options$interval;

            var isNotLoop, _lottieObject, _lottieObject2, nextDirection, _lottieObject3;

            return _regeneratorRuntime().wrap(function _callee$(_context) {
              while (1) {
                switch (_context.prev = _context.next) {
                  case 0:
                    isNotLoop = (shouldLoopWithInterval || shouldLoopWithReverse) === false;

                    if (!(lottieObject == null || isNotLoop)) {
                      _context.next = 4;
                      break;
                    }

                    resolve();
                    return _context.abrupt("return");

                  case 4:
                    _context.next = 6;
                    return delay((_options$interval = options.interval) !== null && _options$interval !== void 0 ? _options$interval : 0);

                  case 6:
                    /*
                     * NOTE(@raon0211):
                     * delay가 실행되는 동안 destroy() 되면 lottieObject가 undefined가 될 수 있다.
                     */
                    if (shouldLoopWithReverse) {
                      nextDirection = lottieObject.playDirection > 0 ? -1 : 1;
                      (_lottieObject = lottieObject) === null || _lottieObject === void 0 ? void 0 : _lottieObject.setDirection(nextDirection);
                      (_lottieObject2 = lottieObject) === null || _lottieObject2 === void 0 ? void 0 : _lottieObject2.play();
                    } else {
                      (_lottieObject3 = lottieObject) === null || _lottieObject3 === void 0 ? void 0 : _lottieObject3.goToAndPlay(0);
                    }

                  case 7:
                  case "end":
                    return _context.stop();
                }
              }
            }, _callee);
          }));

          return function handleComplete() {
            return _ref.apply(this, arguments);
          };
        }();

        lottieObject.addEventListener('complete', handleComplete);
        lottieObject.addEventListener('destroy', resolve);
        lottieObject.play();
      });
    },
    setSpeed: function setSpeed(speed) {
      _speed = speed;

      if (lottieObject === undefined) {
        return;
      }

      lottieObject.setSpeed(speed);
    },
    addEventListener: function addEventListener(eventName, callback) {
      var _lottieObject4;

      (_lottieObject4 = lottieObject) === null || _lottieObject4 === void 0 ? void 0 : _lottieObject4.addEventListener(eventName, callback);
    },
    removeEventListener: function removeEventListener(eventName, callback) {
      var _lottieObject5;

      (_lottieObject5 = lottieObject) === null || _lottieObject5 === void 0 ? void 0 : _lottieObject5.removeEventListener(eventName, callback);
    },
    pause: function pause() {
      if (lottieObject !== undefined) {
        lottieObject.pause();
      }
    },
    destroy: function destroy() {
      if (lottieObject !== undefined) {
        lottieObject.destroy();
        lottieObject = undefined;
      }
    }
  };
}
/**
 * @description
 * 디자이너분들이 로띠를 제작할 때 Lottie json 파일과 Lottie에서 쓰이는 이미지들을 가까운 곳에 모아둡니다.
 * 그렇게 한 뒤 상대 경로를 이용해서 Lottie가 작성됩니다. 이를테면 이런 식입니다:
 * {
 *   "u": "images/",
 *   "p": "brand_list_0.jpg"
 * }
 * 하지만 프론트엔드에서는 상대 경로를 이용해서 이미지를 찾을 수 없어서, 상대 경로를 절대 경로로 바꿔줘야 합니다.
 * 절대 경로로 바꾸기 위해, LottieUrl을 이용해서 images의 basePath를 획득하는 유틸리티입니다.
 *
 * @example
 * input: https://static.toss.im/lotties/Intelligence/brand-list-light.json
 * output: https://static.toss.im/lotties/Intelligence/images/
 */

function getAbsoluteBasePathOfLottieImage(lottieUrl) {
  return "".concat(lottieUrl.split('/').slice(0, -1).join('/'), "/images/");
}

function replaceAssets(originalLottieJSON) {
  var _originalLottieJSON$a;

  var assets = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var meta = arguments.length > 2 ? arguments[2] : undefined;
  return _objectSpread2(_objectSpread2({}, originalLottieJSON), {}, {
    assets: ((_originalLottieJSON$a = originalLottieJSON.assets) !== null && _originalLottieJSON$a !== void 0 ? _originalLottieJSON$a : []).map(function (originalAsset) {
      var replaceableAsset = assets.find(function (asset) {
        return asset.id === originalAsset.id;
      });

      if (replaceableAsset != null) {
        var filePathTokens = replaceableAsset.url.split('/');
        var fileName = filePathTokens.pop();
        var filePath = "".concat(filePathTokens.join('/'), "/");
        return _objectSpread2(_objectSpread2({}, originalAsset), {}, {
          u: filePath,
          p: fileName
        });
      }

      if (meta.lottieUrl != null && originalAsset.u != null) {
        var isAbsolutePath = originalAsset.u.startsWith('http');
        return _objectSpread2(_objectSpread2({}, originalAsset), {}, {
          u: isAbsolutePath ? originalAsset.u : getAbsoluteBasePathOfLottieImage(meta.lottieUrl)
        });
      } else {
        return originalAsset;
      }
    })
  });
}

export { AnimationPlayer };
